import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import * as DropdownMenu from 'components/dropdown-menu';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ToggleCampaignStatusItemProps extends AlertDialog.AlertDialogRootProps {
    campaignId: number;
}

const ToggleCampaignStatusItem = (props: ToggleCampaignStatusItemProps) => {
    const { children, campaignId, ...rest } = props;
    const { success, error } = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign({ campaignId: campaignId });

    const { disablePayments } = campaign;
    const canAcceptPayments = !disablePayments?.active;

    const toggleAction = canAcceptPayments ? 'Disable' : 'Enable';
    const toggleTitle = `${toggleAction} campaign payments`;
    const toggleDescription = `This will ${canAcceptPayments ? 'close' : 'open'} the ability to make payments${
        canAcceptPayments ? ' but' : ' and'
    } keep your page publicly viewable.`;

    const { mutate: updateCampaignStatus, isPending: isUpdatingStatus } = useMutation({
        mutationFn: (disablePayments: boolean) => {
            const data = {
                settings: [
                    {
                        code: Settings.DisablePayments.Code,
                        active: disablePayments,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaignId, data });
        },
        onSuccess: () => {
            success(
                `Campaign payments have been ${canAcceptPayments ? 'disabled' : 'enabled'} successfully.`,
            );

            if (campaign) {
                queryClient.invalidateQueries({
                    queryKey: campaignQueryKeys.campaign(campaignId),
                });

                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.campaigns(campaign.account_id),
                });
            }
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'An unknown error occurred.'),
    });

    return (
        <AlertDialog.Root type={canAcceptPayments ? 'error' : 'success'} {...rest}>
            <AlertDialog.Trigger asChild={true}>
                <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                    {canAcceptPayments ? 'Disable' : 'Enable'} payments
                </DropdownMenu.Item>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>{toggleTitle}</AlertDialog.Title>
                        <AlertDialog.Description>{toggleDescription}</AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            disabled={isUpdatingStatus}
                            onClick={() => updateCampaignStatus(canAcceptPayments)}
                        >
                            {toggleAction}
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { ToggleCampaignStatusItem };
